import usePermission, { ICheckIsPermitted } from './usePermission';
import {
  EntityRole,
  IDashboardInstanceFieldsFragment,
  IDashboardTemplateFieldsFragment,
  IUserPermissionFieldsFragment,
  PermissionKey,
} from '../../apollo/graphqlSchema';
import { freeze } from '@reduxjs/toolkit';
import produce from 'immer';
import { useCallback, useMemo } from 'react';
import useUser from '../useUser';
import { PERMISSION_ERROR_MESSAGE } from '../../../components/Chart/constants/utils';
import {
  IDashboardEntityPermissions,
  IDashboardEntityPermissionsHint,
} from './types';
import { entityRolesLabels } from '../../entityRoles';

const dashboardEntityPermissionsConfig = freeze<
  Record<EntityRole, IDashboardEntityPermissions>
>(
  {
    [EntityRole.Owner]: {
      deleteDashboard: true,
      duplicateDashboard: true,
      manageRoles: true,
      moveAndPublish: true,
      refreshDashboard: true,
      manageSchedules: true,
      viewSchedules: true,
      addReport: true,
      editReport: true,
      deleteReport: true,
      duplicateReport: true,
      replaceReport: true,
      editDashboard: true,
      setDashboardFilter: true,
    },
    [EntityRole.Manager]: {
      deleteDashboard: false,
      duplicateDashboard: true,
      manageRoles: true,
      moveAndPublish: true,
      refreshDashboard: true,
      manageSchedules: true,
      viewSchedules: true,
      editReport: true,
      addReport: true,
      deleteReport: true,
      duplicateReport: true,
      replaceReport: true,
      editDashboard: true,
      setDashboardFilter: true,
    },
    [EntityRole.Editor]: {
      deleteDashboard: false,
      duplicateDashboard: true,
      manageRoles: false,
      moveAndPublish: false,
      refreshDashboard: true,
      manageSchedules: false,
      viewSchedules: true,
      addReport: true,
      deleteReport: true,
      editReport: true,
      replaceReport: true,
      duplicateReport: true,
      editDashboard: true,
      setDashboardFilter: true,
    },
    [EntityRole.Viewer]: {
      deleteDashboard: false,
      duplicateDashboard: true, // everyone can duplicate
      manageRoles: false,
      moveAndPublish: false,
      refreshDashboard: true, // everyone can refresh
      manageSchedules: false,
      viewSchedules: true,
      addReport: false,
      editReport: false,
      deleteReport: false,
      duplicateReport: false,
      replaceReport: false,
      editDashboard: false,
      setDashboardFilter: false,
    },
  },
  true,
);

const historicalPermissions = freeze<IDashboardEntityPermissions>(
  {
    deleteDashboard: false,
    duplicateDashboard: true, // Duplicate is allowed even for historical / filtered dashboards
    manageRoles: false,
    moveAndPublish: false,
    refreshDashboard: false,
    manageSchedules: false,
    viewSchedules: false,
    addReport: false,
    editReport: false,
    replaceReport: false,
    deleteReport: false,
    duplicateReport: false,
    editDashboard: false,
    setDashboardFilter: false,
  },
  true,
);

const permissionKeysByEntityPermission = freeze<
  Record<keyof IDashboardEntityPermissions, PermissionKey[]>
>(
  {
    deleteDashboard: [PermissionKey.DashboardDelete],
    duplicateDashboard: [PermissionKey.DashboardCreate],
    manageRoles: [PermissionKey.ManageAllEntities], // there is a special case for Owner that bypasses this check
    moveAndPublish: [PermissionKey.DashboardMoveTo],
    refreshDashboard: [],
    manageSchedules: [PermissionKey.DashboardScheduleCreate],
    viewSchedules: [PermissionKey.DashboardScheduleRead],
    addReport: [PermissionKey.DashboardEdit],
    editReport: [PermissionKey.DashboardEdit],
    replaceReport: [PermissionKey.DashboardEdit],
    deleteReport: [PermissionKey.DashboardEdit],
    duplicateReport: [PermissionKey.DashboardEdit],
    editDashboard: [PermissionKey.DashboardEdit],
    setDashboardFilter: [PermissionKey.DashboardEdit],
  },
  true,
);

const filteredPermissionHint =
  'This action is not allowed on filtered dashboard';

const historicalPermissionHint =
  'This action is not allowed on historical dashboard';

const entityPermissionsHint = (name: string, role: EntityRole) =>
  `This action is not allowed because you are ${entityRolesLabels[role]} on ${name}`;

// the flag "isLatest" is true for the instance you see when you ask for a dashboard by baseID,
// it is false - for every other case
type IDashboardForPermission =
  | Pick<
      IDashboardInstanceFieldsFragment,
      'permission' | 'isLatestInstance' | 'isDefault' | 'name'
    >
  | Pick<
      IDashboardTemplateFieldsFragment,
      'permission' | 'isLatestVersion' | 'name'
    >;

const getDashboardEntityPermissions = (
  currentLoggedInUser: IUserPermissionFieldsFragment,
  checkIsPermitted: ICheckIsPermitted,
  dashboardWithPermissions: IDashboardForPermission,
): {
  entityRole: EntityRole;
  dashboardEntityPermissions: IDashboardEntityPermissions;
  dashboardEntityPermissionsHint: IDashboardEntityPermissionsHint;
} => {
  const { permission, name } = dashboardWithPermissions;
  const isLatestInstance =
    'isLatestInstance' in dashboardWithPermissions
      ? dashboardWithPermissions.isLatestInstance
      : false;

  // isDefault -> not filtered
  // !isDefault -> filtered
  const isDefault =
    'isDefault' in dashboardWithPermissions
      ? dashboardWithPermissions.isDefault
      : false;

  const isLatestVersion =
    'isLatestVersion' in dashboardWithPermissions
      ? dashboardWithPermissions.isLatestVersion
      : false;

  const isLatest = isLatestInstance || isLatestVersion;
  const { roleAssignments, defaultPublicRole } = permission;

  // Find an Entity Role user assigned to. Use defaultPublicRole as a fallback
  const entityRole =
    roleAssignments.find((roleAssignment) =>
      roleAssignment.users.some((u) => u.id === currentLoggedInUser.id),
    )?.role ?? defaultPublicRole;

  const defaultDashboardEntityPermissions =
    dashboardEntityPermissionsConfig[entityRole];
  const defaultDashboardEntityPermissionsHint: IDashboardEntityPermissionsHint =
    {};

  const { dashboardEntityPermissions, dashboardEntityPermissionsHint } =
    produce(
      {
        dashboardEntityPermissions: defaultDashboardEntityPermissions,
        dashboardEntityPermissionsHint: defaultDashboardEntityPermissionsHint,
      },
      ({
        dashboardEntityPermissions: dashboardEntityPermissionsDraft,
        dashboardEntityPermissionsHint: permissionsHintDraft,
      }) => {
        Object.keys(dashboardEntityPermissionsDraft).forEach((untypedKey) => {
          const entityPermission =
            untypedKey as keyof typeof dashboardEntityPermissionsDraft;

          // (1) "Historical" (i.e. !isLatest) dashboard is either:
          // (a) instance of an old version (e.g. edited dashboard)
          // (b) instance of the current version but there is newer instance (e.g. refreshed dashboard,
          //     the instance before the refresh is the historical, while the new one after refresh is "latest"
          // (c) filtered instance - these are always "historical"
          // In this case we revoke the majority of the permissions using [historicalPermissions] config
          if (!isLatest) {
            const historicalPermission =
              historicalPermissions[entityPermission];
            dashboardEntityPermissionsDraft[entityPermission] =
              historicalPermission;

            // if there is no permission, set the right hint message and return early
            if (!historicalPermission) {
              if (!isDefault) {
                permissionsHintDraft[entityPermission] = filteredPermissionHint;
              } else {
                permissionsHintDraft[entityPermission] =
                  historicalPermissionHint;
              }
              // no need to check the dashboard entity role or user role permissions
              return;
            }
          }

          // (2) Historical dashboard permission is allowed, check entity role permissions
          if (!dashboardEntityPermissionsDraft[entityPermission]) {
            permissionsHintDraft[entityPermission] = entityPermissionsHint(
              name,
              entityRole,
            );
          } else {
            // (3) entity role is allowed, check user role permissions
            const requiredPermissionKeys =
              permissionKeysByEntityPermission[entityPermission];

            if (requiredPermissionKeys.length > 0) {
              // KBT-326: Check for PermissionKey.ManageAllEntities
              // “Manage All Entities” should only enable the user to “Manage Entity Access”.
              if (
                entityRole === EntityRole.Owner &&
                entityPermission === 'manageRoles'
              ) {
                return;
              }
              dashboardEntityPermissionsDraft[entityPermission] =
                checkIsPermitted({
                  requiredPermissionKeys,
                });

              if (!dashboardEntityPermissionsDraft[entityPermission]) {
                permissionsHintDraft[entityPermission] =
                  PERMISSION_ERROR_MESSAGE;
              }
            }
          }
        });
      },
    );

  return {
    entityRole,
    dashboardEntityPermissions,
    dashboardEntityPermissionsHint,
  };
};

export const useDashboardEntityPermissionCheck = () => {
  const user = useUser();
  const { checkIsPermitted } = usePermission();

  return {
    getDashboardEntityPermissions: useCallback(
      (dashboard: IDashboardForPermission) =>
        getDashboardEntityPermissions(user, checkIsPermitted, dashboard),
      [checkIsPermitted, user],
    ),
  };
};

export const useDashboardEntityPermission = (
  dashboard: IDashboardForPermission,
) => {
  const { getDashboardEntityPermissions } = useDashboardEntityPermissionCheck();

  return useMemo(
    () => getDashboardEntityPermissions(dashboard),
    [dashboard, getDashboardEntityPermissions],
  );
};
